/**
 * QR crew signup management (Crew Management page).
 *
 * The "Generate QR Crew Signup" button plus the strip of active codes —
 * each tile shows label, join count, expiry, with view (big scannable QR +
 * PNG download + copy link) and revoke actions. QR rendering is client-side
 * (`qrcode`): the token never touches a third-party image service.
 */

import { useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';
import ConfirmDialog from '../ui/ConfirmDialog';
import {
  createCrewInvite,
  listCrewInvites,
  revokeCrewInvite,
  type CrewInviteLink,
} from '../../api/crewInvites';
import { getErrorMessage } from '../../api/client';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// QR image (shared by the view modal)
// ---------------------------------------------------------------------------

function QrImage({ url, size }: { url: string; size: number }) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  useEffect(() => {
    QRCode.toDataURL(url, { width: size, margin: 2 }).then(setDataUrl).catch(() => setDataUrl(null));
  }, [url, size]);
  if (!dataUrl) return <div style={{ width: size, height: size }} className="bg-gray-100 rounded animate-pulse" />;
  return <img src={dataUrl} width={size} height={size} alt="QR signup code" className="rounded" />;
}

async function downloadPng(link: CrewInviteLink) {
  // High resolution for printing posters/table tents.
  const dataUrl = await QRCode.toDataURL(link.url, { width: 1200, margin: 4 });
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = `programrow-qr-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.png`;
  a.click();
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

export function GenerateQrButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rack"
    >
      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
      </svg>
      Generate QR Crew Signup
    </button>
  );
}

interface QrSignupSectionProps {
  createOpen: boolean;
  onCloseCreate: () => void;
}

export default function QrSignupSection({ createOpen, onCloseCreate }: QrSignupSectionProps) {
  const [links, setLinks] = useState<CrewInviteLink[]>([]);
  const [viewing, setViewing] = useState<CrewInviteLink | null>(null);
  const [revoking, setRevoking] = useState<CrewInviteLink | null>(null);
  const [busy, setBusy] = useState(false);

  // Create form state
  const [label, setLabel] = useState('');
  const [blurb, setBlurb] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const refresh = useCallback(() => {
    listCrewInvites()
      .then(setLinks)
      .catch(() => {
        // Non-fatal: the strip just stays hidden.
      });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const link = await createCrewInvite({
        label: label.trim(),
        blurb: blurb.trim() || undefined,
        expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : undefined,
      });
      toast.success('QR signup code created');
      setLabel('');
      setBlurb('');
      setExpiresAt('');
      onCloseCreate();
      refresh();
      setViewing(link); // straight into the QR so they can print/share it
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    if (!revoking) return;
    setBusy(true);
    try {
      await revokeCrewInvite(revoking.id);
      toast.success('QR code revoked. It stops working immediately');
      setRevoking(null);
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const active = links.filter((l) => l.active);

  return (
    <>
      {/* Active QR tiles */}
      {active.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">Active QR signup codes</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {active.map((link) => (
              <div
                key={link.id}
                className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-3"
              >
                <button
                  type="button"
                  onClick={() => setViewing(link)}
                  className="shrink-0 rounded border border-gray-200 hover:border-primary-400 transition-colors"
                  aria-label={`View QR code for ${link.label}`}
                >
                  <QrImage url={link.url} size={64} />
                </button>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{link.label}</p>
                  <p className="text-xs text-gray-500">
                    {link.useCount} joined
                    {link.expiresAt ? ` · expires ${formatDate(link.expiresAt)}` : ' · no expiry'}
                  </p>
                  <div className="mt-1 flex gap-3 text-xs">
                    <button
                      type="button"
                      onClick={() => setViewing(link)}
                      className="text-primary-600 hover:text-primary-700 font-medium"
                    >
                      View QR
                    </button>
                    <button
                      type="button"
                      onClick={() => setRevoking(link)}
                      className="text-red-600 hover:text-red-700 font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create modal */}
      <Modal isOpen={createOpen} onClose={onCloseCreate} size="md" labelledBy="qr-create-title">
        <form onSubmit={handleCreate} className="p-6">
          <h2 id="qr-create-title" className="text-lg font-semibold text-gray-900 mb-1">
            Generate QR Crew Signup
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Anyone who scans the code can join this organization as crew, until the code expires
            or you delete it.
          </p>
          <div className="space-y-4">
            <div>
              <label htmlFor="qr-label" className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                id="qr-label"
                type="text"
                required
                minLength={2}
                maxLength={80}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Fall 2026 Career Fair"
                autoFocus
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-rack focus:border-rack"
              />
            </div>
            <div>
              <label htmlFor="qr-blurb" className="block text-sm font-medium text-gray-700 mb-1">
                Pitch shown on the signup page{' '}
                <span className="font-normal text-gray-500">(optional)</span>
              </label>
              <textarea
                id="qr-blurb"
                rows={2}
                maxLength={500}
                value={blurb}
                onChange={(e) => setBlurb(e.target.value)}
                placeholder="Crew our Saturday basketball broadcasts: paid, flexible, no experience needed."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-rack focus:border-rack"
              />
            </div>
            <div>
              <label htmlFor="qr-expires" className="block text-sm font-medium text-gray-700 mb-1">
                Expires <span className="font-normal text-gray-500">(optional, end of day)</span>
              </label>
              <input
                id="qr-expires"
                type="date"
                value={expiresAt}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-rack focus:border-rack"
              />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onCloseCreate}
              disabled={busy}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || label.trim().length < 2}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {busy ? 'Creating…' : 'Create QR code'}
            </button>
          </div>
        </form>
      </Modal>

      {/* View modal — the big scannable/printable code */}
      <Modal isOpen={!!viewing} onClose={() => setViewing(null)} size="md" labelledBy="qr-view-title">
        {viewing && (
          <div className="p-6 text-center">
            <h2 id="qr-view-title" className="text-lg font-semibold text-gray-900">
              {viewing.label}
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              {viewing.useCount} joined
              {viewing.expiresAt ? ` · expires ${formatDate(viewing.expiresAt)}` : ''}
            </p>
            <div className="flex justify-center mb-4">
              <QrImage url={viewing.url} size={280} />
            </div>
            <p className="text-xs text-gray-400 break-all mb-4">{viewing.url}</p>
            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={() => downloadPng(viewing)}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              >
                Download PNG
              </button>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(viewing.url);
                  toast.success('Link copied');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Copy link
              </button>
              <button
                type="button"
                onClick={() => setViewing(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Revoke confirm */}
      <ConfirmDialog
        isOpen={!!revoking}
        onClose={() => setRevoking(null)}
        onConfirm={handleRevoke}
        title="Delete this QR code?"
        message={`"${revoking?.label}" will stop working immediately. Anyone scanning a printed copy will see an expired-link page. Crew who already joined are not affected.`}
        confirmLabel="Delete QR code"
        isLoading={busy}
      />
    </>
  );
}
